import { useState, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, Plus, Users, Edit2, Trash2, Shield, ChevronLeft, ChevronRight, X, Loader2, Dices, KeyRound } from 'lucide-react'
import { usersApi, departmentsApi, rolesApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { Badge } from '@/components/common/Badge'
import { ErrorState } from '@/components/common/States'
import { toast } from '@/lib/toast'
import { cn } from '@/lib/utils'
import type { ModuleType } from '@/types'

// ============================================================
// Types
// ============================================================

// The backend returns a role object (not a string enum), but the
// users list also uses a simplified role shape.
interface RoleOption {
  id: string
  name: string
  level: number
  departmentId?: string | null
}

interface DepartmentOption {
  id: string
  name: string
  code: string
}

interface User {
  id: string
  firstName: string
  lastName: string
  email: string
  role: { id: string; name: string; level: number }
  roleId?: string
  departmentId?: string
  secondaryDepartmentId?: string | null
  secondaryRoleId?: string | null
  department?: { id: string; name: string; code: string }
  phone?: string
  isSuperAdmin?: boolean
  preferences?: { visibleModules?: ModuleType[] }
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface UserFormData {
  firstName: string
  lastName: string
  email: string
  password: string
  roleId: string
  departmentId: string
  secondaryDepartmentId: string
  secondaryRoleId: string
  phone: string
  isSuperAdmin: boolean
}

// ============================================================
// Role badge helpers (based on level)
// ============================================================

function getRoleBadgeVariant(level: number): 'neutral' | 'info' | 'primary' | 'warning' | 'success' | 'danger' {
  switch (level) {
    case 1: return 'danger'
    case 2: return 'primary'
    case 3: return 'warning'
    case 4: return 'info'
    default: return 'neutral'
  }
}

// ============================================================
// Constants
// ============================================================

const EMPTY_FORM: UserFormData = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  roleId: '',
  departmentId: '',
  secondaryDepartmentId: '',
  secondaryRoleId: '',
  phone: '',
  isSuperAdmin: false,
}

// Tutti i moduli disponibili
const ALL_MODULES: ModuleType[] = ['SOCIAL', 'ADV', 'EVENTI', 'DIGITAL', 'PR', 'BRANDING', 'CONSULENZA']

// Mapping codice dipartimento → moduli visibili (solo per L4 operativi)
const DEPT_MODULE_MAP: Record<string, ModuleType[]> = {
  MKT:     ['SOCIAL', 'ADV', 'EVENTI'],
  SALES:   ['ADV', 'CONSULENZA', 'EVENTI'],
  DIGITAL: ['DIGITAL', 'ADV', 'SOCIAL'],
  CONTENT: ['SOCIAL', 'BRANDING'],
  PR:      ['PR', 'EVENTI'],
}

const MODULE_LABEL: Record<ModuleType, string> = {
  SOCIAL: 'PED',
  ADV: 'ADV',
  EVENTI: 'Eventi',
  DIGITAL: 'Digital Hub',
  PR: 'PR',
  BRANDING: 'Branding',
  CONSULENZA: 'Consulenza',
  VISUAL_PRODUCTION: 'Visual Production',
  MERCHANDISING: 'Merchandising',
}

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

// Helpers to extract lists from various backend response shapes
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function extractList<T>(data: any, keys: string[]): T[] {
  if (!data) return []
  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key] as T[]
    if (Array.isArray(data?.data?.[key])) return data.data[key] as T[]
    if (Array.isArray(data?.data)) return data.data as T[]
  }
  if (Array.isArray(data)) return data as T[]
  return []
}

// ============================================================
// UserModal
// ============================================================

interface UserModalProps {
  mode: 'create' | 'edit'
  user?: User
  onClose: () => void
  onSuccess: () => void
}

function UserModal({ mode, user, onClose, onSuccess }: UserModalProps) {
  const currentUser = useAuthStore((state) => state.user)
  const currentUserLevel = currentUser?.role?.level ?? 99

  const [form, setForm] = useState<UserFormData>(
    mode === 'edit' && user
      ? {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          password: '',
          roleId: user.role?.id ?? user.roleId ?? '',
          departmentId: user.departmentId ?? '',
          secondaryDepartmentId: user.secondaryDepartmentId ?? '',
          secondaryRoleId: user.secondaryRoleId ?? '',
          phone: user.phone ?? '',
          isSuperAdmin: user.isSuperAdmin ?? false,
        }
      : EMPTY_FORM
  )
  const [error, setError] = useState('')

  // Fetch departments and roles for the dropdowns
  const { data: departmentsData, isLoading: deptsLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: () => departmentsApi.list(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })

  const { data: rolesData, isLoading: rolesLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })

  const departments: DepartmentOption[] = extractList<DepartmentOption>(departmentsData, ['departments', 'data'])
  const roles: RoleOption[] = extractList<RoleOption>(rolesData, ['roles', 'data'])

  // Nascondi Super Admin (L1) se l'utente corrente non e' L1
  // Filtra ruoli in base al dipartimento selezionato
  const availableRoles = roles.filter((r) => {
    // Nascondi L1 se l'utente non è L1
    if (r.level <= 1 && currentUserLevel !== 1) return false
    // Se un dipartimento è selezionato, mostra SOLO i ruoli di quel dipartimento
    if (form.departmentId) {
      return r.departmentId === form.departmentId
    }
    return true
  })
  const sortedRoles = [...availableRoles].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))

  // Reset roleId se il ruolo selezionato non è più disponibile dopo cambio dipartimento
  useEffect(() => {
    if (form.roleId && availableRoles.length > 0 && !availableRoles.find((r) => r.id === form.roleId)) {
      setForm((p) => ({ ...p, roleId: '' }))
    }
  }, [form.departmentId]) // eslint-disable-line react-hooks/exhaustive-deps

  // a11y: chiusura con ESC + focus iniziale sul primo campo
  const firstFieldRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    firstFieldRef.current?.focus()
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  function extractError(err: unknown, fallback: string): string {
    const axErr = err as { response?: { data?: { error?: { message?: string; details?: { campo: string; messaggio: string }[] } } } }
    const details = axErr?.response?.data?.error?.details
    if (details && details.length > 0) {
      return details.map((d) => d.messaggio).join('. ')
    }
    return axErr?.response?.data?.error?.message ?? (err instanceof Error ? err.message : fallback)
  }

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => usersApi.create(data),
    onSuccess: () => { onSuccess(); onClose() },
    onError: (err: unknown) => setError(extractError(err, 'Errore durante la creazione')),
    meta: { silent: true },
  })

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => usersApi.update(user!.id, data),
    onSuccess: () => { onSuccess(); onClose() },
    onError: (err: unknown) => setError(extractError(err, 'Errore durante il salvataggio')),
    meta: { silent: true },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!form.roleId) { setError('Il ruolo e\u2019 obbligatorio'); return }
    if (!form.departmentId) { setError('Il dipartimento e\u2019 obbligatorio'); return }

    const selectedRole = roles.find((r) => r.id === form.roleId)
    const selectedLevel = selectedRole?.level ?? 4
    const selectedDept = departments.find((d) => d.id === form.departmentId)

    // L2 (Admin) e L3 (Manager): tutti i moduli — supervisionano progetti trasversali
    // L4 (Operativi): solo moduli del proprio dipartimento
    const modules = selectedLevel <= 3
      ? ALL_MODULES
      : (selectedDept ? (DEPT_MODULE_MAP[selectedDept.code] ?? []) : [])

    const payload: Record<string, unknown> = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      roleId: form.roleId,
      departmentId: form.departmentId,
      secondaryDepartmentId: form.secondaryDepartmentId || null,
      secondaryRoleId: form.secondaryRoleId || null,
      isSuperAdmin: form.isSuperAdmin,
    }
    if (form.phone) payload.phone = form.phone

    // Salva preferences con visibleModules
    if (modules.length > 0) {
      payload.preferences = { visibleModules: modules }
    } else {
      payload.preferences = {}
    }

    if (mode === 'create') {
      if (!form.password) { setError('La password e\u2019 obbligatoria'); return }
      payload.password = form.password
      createMutation.mutate(payload)
    } else {
      if (form.password) payload.password = form.password
      updateMutation.mutate(payload)
    }
  }

  function set(field: keyof Omit<UserFormData, 'roleId'>) {
    return (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((p) => ({ ...p, [field]: e.target.value }))
  }

  const levelLabel: Record<number, string> = {
    1: 'Livello 1 — Super Admin',
    2: 'Livello 2 — Admin',
    3: 'Livello 3 — Manager',
    4: 'Livello 4 — Operativo',
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-modal-title"
        className="relative bg-white rounded-3xl shadow-modal w-full max-w-md z-10 overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2.5">
            <div className="flex items-center justify-center w-7 h-7 bg-primary-50 rounded-lg">
              <Shield className="w-4 h-4 text-primary-600" />
            </div>
            <h2 id="user-modal-title" className="text-base font-semibold text-slate-900 tracking-tight">
              {mode === 'create' ? 'Nuovo Utente' : 'Modifica Utente'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Nome *</label>
              <input
                ref={firstFieldRef}
                type="text"
                className={inputClass}
                value={form.firstName}
                onChange={set('firstName')}
                placeholder="Mario"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Cognome *</label>
              <input
                type="text"
                className={inputClass}
                value={form.lastName}
                onChange={set('lastName')}
                placeholder="Rossi"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">Email *</label>
            <input
              type="email"
              className={inputClass}
              value={form.email}
              onChange={set('email')}
              placeholder="mario.rossi@equipe.it"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              {mode === 'create' ? 'Password *' : 'Nuova Password (lascia vuoto per non cambiare)'}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                className={inputClass + ' flex-1'}
                value={form.password}
                onChange={set('password')}
                placeholder={mode === 'create' ? 'Minimo 8 caratteri' : 'Lascia vuoto per non cambiare'}
                required={mode === 'create'}
              />
              <button
                type="button"
                title="Genera password"
                onClick={() => {
                  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%'
                  const arr = new Uint32Array(8)
                  crypto.getRandomValues(arr)
                  const pwd = Array.from(arr, (v) => chars[v % chars.length]).join('')
                  setForm((p) => ({ ...p, password: pwd }))
                }}
                className="px-3 py-2 bg-white text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-900 transition-colors flex-shrink-0"
              >
                <Dices className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Dipartimento + Telefono */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                Dipartimento *
                {deptsLoading && <Loader2 className="inline w-3 h-3 ml-1.5 animate-spin text-slate-400" />}
              </label>
              {deptsLoading ? (
                <div className={cn(inputClass, 'animate-pulse bg-slate-50 text-slate-400')}>
                  Caricamento...
                </div>
              ) : departments.length > 0 ? (
                <select
                  className={inputClass}
                  value={form.departmentId}
                  onChange={set('departmentId')}
                  required
                >
                  <option value="">-- Seleziona --</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  className={inputClass}
                  value={form.departmentId}
                  onChange={set('departmentId')}
                >
                  <option value="">-- Seleziona --</option>
                  <option value="marketing">Marketing</option>
                  <option value="sales">Sales</option>
                  <option value="digital">Digital Hub</option>
                  <option value="content">Content</option>
                </select>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Telefono</label>
              <input
                type="tel"
                className={inputClass}
                value={form.phone}
                onChange={set('phone')}
                placeholder="+39 02 1234567"
              />
            </div>
          </div>

          {/* Ruolo (filtrato per dipartimento) */}
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1.5">
              Ruolo *
              {rolesLoading && <Loader2 className="inline w-3 h-3 ml-1.5 animate-spin text-slate-400" />}
            </label>
            {rolesLoading ? (
              <div className={cn(inputClass, 'animate-pulse bg-slate-50 text-slate-400')}>
                Caricamento ruoli...
              </div>
            ) : !form.departmentId ? (
              <div className={cn(inputClass, 'text-slate-400 bg-slate-50 cursor-not-allowed')}>
                Seleziona prima il dipartimento
              </div>
            ) : (
              <select
                className={inputClass}
                value={form.roleId}
                onChange={(e) => setForm((p) => ({ ...p, roleId: e.target.value }))}
                required
              >
                <option value="">-- Seleziona ruolo --</option>
                {sortedRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                    {levelLabel[role.level] != null ? ` (${(levelLabel[role.level] ?? '').split('—')[1]?.trim()})` : ''}
                  </option>
                ))}
              </select>
            )}
            {form.departmentId && sortedRoles.length === 0 && !rolesLoading && (
              <p className="text-xs text-warning-600 mt-1">
                Nessun ruolo disponibile per questo dipartimento
              </p>
            )}
          </div>

          {/* Dipartimento 2 + Ruolo nel Dipartimento 2 (opzionale) */}
          {form.departmentId && departments.length > 1 && (
            <>
              <div className="border-t border-slate-100 pt-4">
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Dipartimento 2 <span className="text-slate-400 font-normal">(opzionale)</span>
                </label>
                <select
                  className={inputClass}
                  value={form.secondaryDepartmentId}
                  onChange={(e) => setForm((p) => ({ ...p, secondaryDepartmentId: e.target.value, secondaryRoleId: '' }))}
                >
                  <option value="">-- Nessuno --</option>
                  {departments
                    .filter((d) => d.id !== form.departmentId)
                    .map((dept) => (
                      <option key={dept.id} value={dept.id}>
                        {dept.name}
                      </option>
                    ))}
                </select>
              </div>

              {form.secondaryDepartmentId && (
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1.5">
                    Ruolo nel Dipartimento 2 *
                  </label>
                  {(() => {
                    const secondaryRoles = roles
                      .filter((r) => r.departmentId === form.secondaryDepartmentId)
                      .filter((r) => r.level > 1 || currentUserLevel === 1)
                      .sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))
                    return secondaryRoles.length > 0 ? (
                      <select
                        className={inputClass}
                        value={form.secondaryRoleId}
                        onChange={(e) => setForm((p) => ({ ...p, secondaryRoleId: e.target.value }))}
                        required
                      >
                        <option value="">-- Seleziona ruolo --</option>
                        {secondaryRoles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                            {levelLabel[role.level] != null ? ` (${(levelLabel[role.level] ?? '').split('—')[1]?.trim()})` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <p className="text-xs text-warning-600">
                        Nessun ruolo disponibile per questo dipartimento
                      </p>
                    )
                  })()}
                </div>
              )}
            </>
          )}

          {/* Super Admin toggle (solo per Super Admin) */}
          {currentUser?.isSuperAdmin && (
            <label className={cn(
              'flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
              form.isSuperAdmin ? 'border-danger-300 bg-danger-50' : 'border-slate-200 hover:border-slate-300'
            )}>
              <input
                type="checkbox"
                checked={form.isSuperAdmin}
                onChange={(e) => setForm((p) => ({ ...p, isSuperAdmin: e.target.checked }))}
                className="w-4 h-4 accent-danger-600 rounded"
              />
              <div>
                <span className="text-sm font-medium text-slate-800">Super Admin</span>
                <p className="text-xs text-slate-500 mt-0.5">Accesso completo a tutte le funzionalità del gestionale</p>
              </div>
            </label>
          )}

          {/* Moduli Visibili (read-only, derivati dal ruolo + dipartimento) */}
          {(() => {
            const selectedRole = roles.find((r) => r.id === form.roleId)
            const selectedLevel = selectedRole?.level ?? 0
            if (!selectedLevel || !form.departmentId) return null
            const selectedDept = departments.find((d) => d.id === form.departmentId)
            // L2/L3: tutti i moduli — supervisionano progetti trasversali
            // L4: solo moduli del dipartimento
            const mods = selectedLevel <= 3
              ? ALL_MODULES
              : (selectedDept ? (DEPT_MODULE_MAP[selectedDept.code] ?? []) : [])
            if (mods.length === 0) return null
            return (
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1.5">
                  Moduli Visibili
                </label>
                <p className="text-xs text-slate-400 mb-2">
                  {selectedLevel <= 3
                    ? 'Tutti i moduli — ruolo di supervisione.'
                    : `In base al dipartimento ${selectedDept?.name ?? ''}.`}
                </p>
                <div className="flex flex-wrap gap-2">
                  {mods.map((mod) => (
                    <span
                      key={mod}
                      className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md bg-slate-100 text-slate-600"
                    >
                      {MODULE_LABEL[mod]}
                    </span>
                  ))}
                </div>
              </div>
            )
          })()}

          {error && (
            <p className="text-xs text-danger-600 bg-danger-50 border border-danger-200 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
            >
              {isPending ? 'Salvataggio...' : mode === 'create' ? 'Crea Utente' : 'Salva Modifiche'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ============================================================
// DeleteConfirmDialog
// ============================================================

interface DeleteDialogProps {
  user: User
  allUsers: User[]
  onClose: () => void
  onConfirm: (replacementId?: string) => void
  isPending: boolean
}

function DeleteDialog({ user, allUsers, onClose, onConfirm, isPending }: DeleteDialogProps) {
  const [step, setStep] = useState<'check' | 'confirm'>('check')
  const [action, setAction] = useState<'replace' | 'remove'>('remove')
  const [replacementId, setReplacementId] = useState('')

  const { data: projectsData, isLoading: loadingProjects } = useQuery({
    queryKey: ['projects', { involvedUserId: user.id, limit: 50 }],
    queryFn: () => import('@/services/api').then(m => m.projectsApi.list({ involvedUserId: user.id, limit: 50 })),
  })
  const raw = projectsData as any
  const inner = raw?.data ?? raw
  const projects: { id: string; name: string; status: string }[] = inner?.items ?? []

  const otherUsers = allUsers.filter((u) => u.id !== user.id && u.isActive)
  const hasProjects = projects.length > 0

  // Se non ha progetti, vai diretto alla conferma
  useEffect(() => {
    if (!loadingProjects && !hasProjects) setStep('confirm')
  }, [loadingProjects, hasProjects])

  // a11y: chiusura con ESC
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-user-title"
        className="relative bg-white rounded-3xl shadow-modal w-full max-w-md z-10 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="flex items-center justify-center w-9 h-9 bg-danger-50 rounded-lg flex-shrink-0">
            <Trash2 className="w-5 h-5 text-danger-600" />
          </div>
          <div>
            <h3 id="delete-user-title" className="text-base font-semibold text-slate-900 tracking-tight">Elimina Utente</h3>
            <p className="text-xs text-slate-500">Questa azione non puo' essere annullata</p>
          </div>
        </div>

        {loadingProjects ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            <span className="ml-2 text-sm text-slate-500">Controllo progetti collegati...</span>
          </div>
        ) : step === 'check' && hasProjects ? (
          <>
            {/* Alert progetti collegati */}
            <div className="mb-4 p-3 bg-warning-50 border border-warning-200 rounded-lg">
              <p className="text-xs font-semibold text-warning-800 mb-2">
                Attenzione: {user.firstName} {user.lastName} e' coinvolto/a in {projects.length} progett{projects.length === 1 ? 'o' : 'i'}:
              </p>
              <ul className="space-y-1 mb-3">
                {projects.slice(0, 6).map((p) => (
                  <li key={p.id} className="text-xs text-warning-700 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-warning-400 flex-shrink-0" />
                    {p.name}
                  </li>
                ))}
                {projects.length > 6 && (
                  <li className="text-xs text-warning-600 italic">...e altri {projects.length - 6}</li>
                )}
              </ul>
            </div>

            {/* Scelta azione */}
            <p className="text-sm font-medium text-slate-800 mb-3">Cosa vuoi fare con i progetti?</p>
            <div className="space-y-2 mb-5">
              <label className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                action === 'replace' ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-slate-300'
              )}>
                <input type="radio" name="action" checked={action === 'replace'} onChange={() => setAction('replace')} className="mt-0.5 accent-primary-600" />
                <div>
                  <span className="text-sm font-medium text-slate-800">Sostituisci con un altro responsabile</span>
                  <p className="text-xs text-slate-500 mt-0.5">I progetti verranno riassegnati a un'altra persona</p>
                </div>
              </label>
              <label className={cn(
                'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                action === 'remove' ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-slate-300'
              )}>
                <input type="radio" name="action" checked={action === 'remove'} onChange={() => setAction('remove')} className="mt-0.5 accent-primary-600" />
                <div>
                  <span className="text-sm font-medium text-slate-800">Rimuovi dai progetti senza sostituto</span>
                  <p className="text-xs text-slate-500 mt-0.5">I progetti resteranno senza responsabile</p>
                </div>
              </label>
            </div>

            {/* Dropdown sostituto */}
            {action === 'replace' && (
              <div className="mb-5">
                <label className="block text-xs font-medium text-slate-700 mb-1.5">Nuovo responsabile</label>
                <select
                  value={replacementId}
                  onChange={(e) => setReplacementId(e.target.value)}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                >
                  <option value="">-- Seleziona --</option>
                  {otherUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName} — {u.role.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                Annulla
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={action === 'replace' && !replacementId}
                className="px-4 py-2 text-sm font-medium text-white bg-danger-600 rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors"
              >
                Continua
              </button>
            </div>
          </>
        ) : (
          /* Step finale: conferma eliminazione */
          <>
            <p className="text-sm text-slate-700 mb-2">
              Sei sicuro di voler eliminare definitivamente{' '}
              <span className="font-medium">{user.firstName} {user.lastName}</span>?
            </p>
            {hasProjects && action === 'replace' && replacementId && (
              <p className="text-xs text-slate-500 mb-4">
                I {projects.length} progetti verranno riassegnati a{' '}
                <span className="font-medium">{otherUsers.find((u) => u.id === replacementId)?.firstName} {otherUsers.find((u) => u.id === replacementId)?.lastName}</span>.
              </p>
            )}
            {hasProjects && action === 'remove' && (
              <p className="text-xs text-slate-500 mb-4">
                I {projects.length} progetti resteranno senza responsabile.
              </p>
            )}
            <div className="flex items-center justify-end gap-3 mt-4">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                Annulla
              </button>
              {hasProjects && (
                <button onClick={() => setStep('check')} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors">
                  Indietro
                </button>
              )}
              <button
                onClick={() => onConfirm(action === 'replace' ? replacementId : undefined)}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-danger-600 rounded-lg hover:bg-danger-700 disabled:opacity-50 transition-colors"
              >
                {isPending ? 'Eliminazione...' : 'Elimina Definitivamente'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ============================================================
// UsersAdminPage
// ============================================================

export function UsersAdminPage() {
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((state) => state.user)

  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterRole, setFilterRole] = useState('')
  const [page, setPage] = useState(1)
  const limit = 20

  // Fetch roles for the filter dropdown (in the toolbar)
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => rolesApi.list(),
    staleTime: 10 * 60 * 1000,
    retry: 1,
  })
  const roles: RoleOption[] = extractList<RoleOption>(rolesData, ['roles', 'data'])
  const sortedRoles = [...roles].sort((a, b) => a.level - b.level || a.name.localeCompare(b.name))

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 350)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [search])

  const [showCreate, setShowCreate] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deletingUser, setDeletingUser] = useState<User | null>(null)
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, string | null>>({})
  const [loadingPassword, setLoadingPassword] = useState<string | null>(null)

  const queryParams: Record<string, unknown> = {
    page,
    limit,
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(filterRole ? { roleId: filterRole } : {}),
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['users', queryParams],
    queryFn: () => usersApi.list(queryParams),
    retry: false,
    placeholderData: (prev) => prev,
  })

  // L'interceptor Axios fa unwrap { success, data } → data arriva già come { users, pagination }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = data as any
  const responseData = raw?.data ?? raw // gestisce sia { data: { users } } sia { users } diretto
  const users: User[] = responseData?.users ?? responseData?.items ?? []
  const paginationData = responseData?.pagination
  const total: number = paginationData?.total ?? 0
  const lastPage: number = paginationData?.totalPages ?? 1

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['users'] })
  }

  const deleteMutation = useMutation({
    mutationFn: ({ id, replacementId }: { id: string; replacementId?: string }) => usersApi.hardDelete(id, replacementId),
    onSuccess: () => {
      invalidate()
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setDeletingUser(null)
      toast.success('Utente eliminato')
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      usersApi.update(id, { isActive }),
    // Aggiornamento ottimistico: il badge cambia subito, con rollback in caso di errore
    onMutate: async ({ id, isActive }) => {
      await queryClient.cancelQueries({ queryKey: ['users'] })
      const previous = queryClient.getQueriesData({ queryKey: ['users'] })
      queryClient.setQueriesData({ queryKey: ['users'] }, (old: unknown) => {
        if (!old || typeof old !== 'object') return old
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const o = old as any
        const container = o.data ?? o
        const list: User[] | undefined = container?.users ?? container?.items
        if (!Array.isArray(list)) return old
        const updated = list.map((u) => (u.id === id ? { ...u, isActive } : u))
        if (container?.users) return o.data ? { ...o, data: { ...o.data, users: updated } } : { ...o, users: updated }
        return o.data ? { ...o, data: { ...o.data, items: updated } } : { ...o, items: updated }
      })
      return { previous }
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous?.forEach(([key, value]) => queryClient.setQueryData(key, value))
    },
    onSettled: () => invalidate(),
  })

  const canManage = (currentUser?.role?.level ?? 99) <= 1

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Gestione Utenti</h1>
          <p className="text-sm text-slate-500 mt-1">
            Amministra gli account del team (<span className="tabular-nums">{total}</span> totali)
          </p>
        </div>
        {canManage && (
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Nuovo Utente
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Cerca per nome o email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 placeholder:text-slate-400"
          />
        </div>

        <select
          value={filterRole}
          onChange={(e) => { setFilterRole(e.target.value); setPage(1) }}
          className="px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
        >
          <option value="">Tutti i ruoli</option>
          {sortedRoles.map((r) => (
            <option key={r.id} value={r.id}>{r.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-card overflow-hidden">
        <div className="overflow-x-auto">
        {/* Table header */}
        <div className="grid grid-cols-[1fr_140px_160px_80px_96px] gap-4 px-4 py-2.5 border-b border-slate-200 bg-slate-50 min-w-[760px]">
          <span className="text-xs font-medium text-subtle uppercase tracking-wide">Nome</span>
          <span className="text-xs font-medium text-subtle uppercase tracking-wide">Email</span>
          <span className="text-xs font-medium text-subtle uppercase tracking-wide">Ruolo</span>
          <span className="text-xs font-medium text-subtle uppercase tracking-wide">Stato</span>
          <span className="text-xs font-medium text-subtle uppercase tracking-wide text-right">Azioni</span>
        </div>

        {/* Loading skeleton */}
        {isLoading && (
          <div className="divide-y divide-slate-200 min-w-[760px]">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="grid grid-cols-[1fr_140px_160px_80px_96px] gap-4 px-4 py-3 items-center">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-slate-100 rounded-full animate-pulse flex-shrink-0" />
                  <div className="space-y-1.5">
                    <div className="h-3 bg-slate-100 rounded animate-pulse w-28" />
                    <div className="h-2.5 bg-slate-100 rounded animate-pulse w-20" />
                  </div>
                </div>
                <div className="h-3 bg-slate-100 rounded animate-pulse w-32" />
                <div className="h-5 bg-slate-100 rounded-full animate-pulse w-24" />
                <div className="h-5 bg-slate-100 rounded-full animate-pulse w-12" />
                <div className="flex justify-end gap-2">
                  <div className="h-7 w-7 bg-slate-100 rounded animate-pulse" />
                  <div className="h-7 w-7 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error state */}
        {!isLoading && isError && (
          <ErrorState
            title="Impossibile caricare gli utenti"
            description="Si è verificato un errore nel recupero della lista. Riprova."
            onRetry={() => { void refetch() }}
          />
        )}

        {/* Empty state */}
        {!isLoading && !isError && users.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="p-4 bg-slate-50 rounded-full mb-4">
              <Users className="w-8 h-8 text-slate-300" />
            </div>
            <h3 className="text-sm font-medium text-slate-700 mb-1">
              {debouncedSearch || filterRole ? 'Nessun utente trovato' : 'Nessun utente ancora'}
            </h3>
            <p className="text-sm text-slate-400">
              {debouncedSearch || filterRole
                ? 'Prova a modificare i filtri di ricerca'
                : 'Aggiungi il primo utente per iniziare'}
            </p>
          </div>
        )}

        {/* Table rows */}
        {!isLoading && !isError && users.length > 0 && (
          <div className="divide-y divide-slate-200 min-w-[760px]">
            {users.map((user) => {
              const initials = `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
              const roleName = user.role?.name ?? '—'
              const roleLevel = user.role?.level ?? 4
              return (
                <div
                  key={user.id}
                  className="grid grid-cols-[1fr_140px_160px_80px_96px] gap-4 px-4 py-3 hover:bg-slate-50 transition-colors items-center"
                >
                  {/* Name */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-semibold text-white">{initials}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium text-slate-900 truncate">
                          {user.firstName} {user.lastName}
                        </p>
                        {user.isSuperAdmin && (
                          <span title="Super Admin" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-warning-50 text-warning-600 rounded-md text-[10px] font-semibold flex-shrink-0">
                            <Shield className="w-3 h-3" />
                            SA
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    </div>
                  </div>

                  {/* Email / Password */}
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 truncate">{user.email}</p>
                    {currentUser?.isSuperAdmin && (
                      revealedPasswords[user.id] !== undefined ? (
                        <p className="text-xs text-warning-600 font-mono mt-0.5 truncate tabular-nums">
                          {revealedPasswords[user.id] ?? 'N/D'}
                        </p>
                      ) : (
                        <button
                          onClick={async () => {
                            setLoadingPassword(user.id)
                            try {
                              const res = await usersApi.getPassword(user.id)
                              setRevealedPasswords((p) => ({ ...p, [user.id]: res.password }))
                            } catch { setRevealedPasswords((p) => ({ ...p, [user.id]: null })) }
                            setLoadingPassword(null)
                          }}
                          disabled={loadingPassword === user.id}
                          className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-primary-600 mt-0.5 transition-colors"
                        >
                          {loadingPassword === user.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <KeyRound className="w-3 h-3" />}
                          Mostra password
                        </button>
                      )
                    )}
                  </div>

                  {/* Role */}
                  <div>
                    <Badge variant={getRoleBadgeVariant(roleLevel)} size="sm">
                      {roleName}
                    </Badge>
                    {user.department?.name && (
                      <p className="text-xs text-slate-400 mt-0.5 truncate">{user.department.name}</p>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <button
                      onClick={() => toggleActiveMutation.mutate({ id: user.id, isActive: !user.isActive })}
                      disabled={!canManage || toggleActiveMutation.isPending}
                      title={user.isActive ? 'Clicca per disattivare' : 'Clicca per attivare'}
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors',
                        canManage && 'cursor-pointer',
                        user.isActive
                          ? 'bg-success-50 text-success-700 hover:bg-success-100'
                          : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                      )}
                    >
                      <span className={cn('w-1.5 h-1.5 rounded-full', user.isActive ? 'bg-success-500' : 'bg-slate-400')} />
                      {user.isActive ? 'Attivo' : 'Inattivo'}
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-1">
                    {canManage && (
                      <>
                        <button
                          onClick={() => setEditingUser(user)}
                          title="Modifica"
                          className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded-md transition-colors"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setDeletingUser(user)}
                          title="Elimina"
                          className="p-1.5 text-slate-400 hover:text-danger-600 hover:bg-danger-50 rounded-md transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
        </div>

        {/* Pagination */}
        {!isLoading && lastPage > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <p className="text-xs text-slate-500 tabular-nums">
              Pagina {page} di {lastPage} ({total} utenti)
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white border border-transparent hover:border-slate-200 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, lastPage) }).map((_, i) => {
                const pageNum = i + 1
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={cn(
                      'w-7 h-7 text-xs rounded-md transition-colors tabular-nums',
                      page === pageNum
                        ? 'bg-primary-600 text-white font-medium'
                        : 'text-slate-600 hover:bg-white hover:border hover:border-slate-200'
                    )}
                  >
                    {pageNum}
                  </button>
                )
              })}
              <button
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                disabled={page === lastPage}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-white border border-transparent hover:border-slate-200 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <UserModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSuccess={invalidate}
        />
      )}

      {/* Edit modal */}
      {editingUser && (
        <UserModal
          mode="edit"
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={invalidate}
        />
      )}

      {/* Delete confirmation */}
      {deletingUser && (
        <DeleteDialog
          user={deletingUser}
          allUsers={users}
          onClose={() => setDeletingUser(null)}
          onConfirm={(replacementId) => deleteMutation.mutate({ id: deletingUser.id, ...(replacementId ? { replacementId } : {}) })}
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  )
}
